export type WorkforceReductionComplianceAssessmentStatusDto =
  | 'ready'
  | 'blocked'
  | 'needs_review'
  | 'requires_legal_review'
  | 'unsupported';

export type WorkforceReductionComplianceOwnerDto = 'legal' | 'hr_policy_owner' | 'hr_ops';

export type WorkforceReductionComplianceScopeModeDto =
  | 'single_establishment'
  | 'multi_establishment'
  | 'ambiguous';

export type WorkforceReductionJurisdictionProfileDto = {
  countryCode: string | null;
  stateOrProvinceCode: string | null;
  legalEntityIds: string[];
  establishmentIds: string[];
  effectiveDate: string | null;
  supportedJurisdictionKey: string | null;
  supported: boolean;
  scopeMode: WorkforceReductionComplianceScopeModeDto;
  dataGaps: string[];
};

export type WorkforceReductionPublishedPolicyPackDto = {
  policyPackId: string;
  policyPackVersion: string;
  label: string;
  jurisdictionKey: string;
  policyType: 'warn_notice' | 'collective_consultation';
  publishedAt: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  ruleIds: string[];
  sourceArtifactIds: string[];
  sourceLocatorIds?: string[];
  clauseIds: string[];
  publishedPolicyPackId?: string | null;
  approvalRecordId?: string | null;
};

export type WorkforceReductionComplianceCitationProvenanceModeDto =
  | 'governed_published_pack'
  | 'bridged_runtime'
  | 'unavailable';

export type WorkforceReductionComplianceRuleVersionDto = {
  ruleId: string;
  ruleVersion: string;
  label: string;
  policyPackId: string;
  jurisdictionKey: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  sourceArtifactIds: string[];
  clauseIds: string[];
};

export type WorkforceReductionComplianceCitationDto = {
  citationId: string;
  label: string;
  sourceArtifactId: string;
  clauseId: string | null;
  ruleId: string | null;
  policyPackId: string;
  policyPackVersion: string;
  provenanceMode?: WorkforceReductionComplianceCitationProvenanceModeDto;
  publishedPolicyPackId?: string | null;
  approvalRecordId?: string | null;
  sourceLocatorId?: string | null;
  sourceLocatorLabel?: string | null;
  sourceDocumentTitle?: string | null;
  sourceDocumentFilename?: string | null;
  sourceDocumentSha256?: string | null;
  canonicalUrl?: string | null;
  canonicalOrigin?: string | null;
  pageNumberStart?: number | null;
  pageNumberEnd?: number | null;
  sectionLabel?: string | null;
  excerpt?: string | null;
};

export type WorkforceReductionComplianceBlockerDto = {
  blockerId: string;
  title: string;
  detail: string;
  owner: WorkforceReductionComplianceOwnerDto;
  ruleId: string | null;
  citationIds: string[];
};

export type WorkforceReductionComplianceObligationDto = {
  actionId: string;
  title: string;
  detail: string | null;
  owner: WorkforceReductionComplianceOwnerDto;
  ruleId: string | null;
  citationIds: string[];
};

export type WorkforceReductionComplianceDeadlineDto = {
  deadlineId: string;
  title: string;
  dueAt: string;
  owner: WorkforceReductionComplianceOwnerDto;
  ruleId: string | null;
  citationIds: string[];
};

export type WorkforceReductionComplianceMissingDataDto = {
  field: string;
  message: string;
  owner: WorkforceReductionComplianceOwnerDto;
};

export type WorkforceReductionComplianceProofDto = {
  proofId: string;
  engineVersion: string;
  inputHash: string;
  snapshotHash: string;
  policyPackIds: string[];
  publishedPolicyPackIds?: string[];
  ruleIds: string[];
  sourceArtifactIds: string[];
  sourceLocatorIds?: string[];
  clauseIds: string[];
  approvalRecordIds?: string[];
  generatedAt: string;
};

export type WorkforceReductionPolicyTypeDto =
  | 'warn_notice'
  | 'collective_consultation'
  | 'owbpa_disclosure'
  | 'custom';

export type WorkforceReductionPolicySourceAuthorityLayerDto =
  | 'LEGAL_FLOOR'
  | 'LEGAL_DETAIL'
  | 'REGULATORY_GUIDANCE'
  | 'TENANT_POLICY'
  | 'TENANT_PROCESS';

export type WorkforceReductionPolicySourceAuthorityTypeDto =
  | 'STATUTE'
  | 'REGULATION'
  | 'GUIDANCE'
  | 'POLICY'
  | 'PROCESS'
  | 'OTHER';

export type WorkforceReductionPolicyLifecycleStateDto = 'uploaded' | 'reviewed' | 'approved' | 'published';

export type WorkforceReductionPolicyReviewStatusDto = 'pending' | 'changes_requested' | 'approved';

export type WorkforceReductionPolicyPackKindDto = 'legal_floor' | 'tenant_overlay';

export type WorkforceReductionPolicySourceLocatorRecordDto = {
  locatorId: string;
  locatorLabel: string;
  pageNumberStart: number;
  pageNumberEnd: number | null;
  sectionLabel: string | null;
  excerpt: string;
  excerptHash: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Which tenant OWNS a control-plane row that a tenant-scoped read returned.
 *
 * Policy reads span `[tenantId, 'system']`, so a tenant's read legitimately
 * contains inherited system rows alongside its own. Only the server knows which
 * is which, and a client must not infer it by reading the system tenant
 * directly: single-tenant deployments configure `HELIX_TENANT_ID` and the auth
 * guard rejects any `x-company-id` that differs from it, so a cross-scope read
 * is a 401, not an answer.
 *
 * - `tenant` — owned by the reading tenant; writable and reusable by it.
 * - `system` — inherited, read-only for this tenant. Never reuse such a row as
 *   the target of a tenant write; create the tenant's own row instead.
 */
export type WorkforceReductionPolicyOwnerScopeDto = 'tenant' | 'system';

export type WorkforceReductionPolicySourceArtifactRecordDto = {
  artifactId: string;
  /** Owning scope of this row — see WorkforceReductionPolicyOwnerScopeDto. */
  ownerScope: WorkforceReductionPolicyOwnerScopeDto;
  title: string;
  filename: string;
  mimeType: string;
  sha256: string;
  canonicalUrl: string;
  canonicalOrigin: string;
  countryCode: string | null;
  subnationalCode: string | null;
  jurisdictionKey: string;
  policyType: WorkforceReductionPolicyTypeDto;
  sourceAuthorityLayer: WorkforceReductionPolicySourceAuthorityLayerDto;
  sourceAuthorityType: WorkforceReductionPolicySourceAuthorityTypeDto;
  effectiveDate: string | null;
  effectiveAsOf: string;
  capturedAt: string;
  lifecycleState: WorkforceReductionPolicyLifecycleStateDto;
  reviewStatus: WorkforceReductionPolicyReviewStatusDto;
  ownerUserId: string | null;
  reviewerUserId: string | null;
  approverUserId: string | null;
  locators: WorkforceReductionPolicySourceLocatorRecordDto[];
  createdAt: string;
  updatedAt: string;
};

export type WorkforceReductionPolicyClauseRecordDto = {
  clauseId: string;
  clauseKey: string;
  label: string;
  citationLabel: string;
  sourceArtifactId: string;
  sourceLocatorId: string;
  normalizedText: string;
  approvedForPublication: boolean;
  approvalRecordId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Binds each executable threshold in a rule's `ruleJson` to the index of the
 * clause/artifact/locator triple that supports it.
 *
 * The triples say which sources back the rule; this says which source backs
 * which NUMBER. Keys are field paths into `ruleJson`
 * (`minimumAffectedEmployees`, `tiers.0.consultationDays`); values are indexes
 * into the rule's own `clauseIds` / `sourceArtifactIds` / `sourceLocatorIds`
 * arrays, so the binding reuses the existing provenance authority instead of
 * naming sources a second time and risking divergence.
 */
export type WorkforceReductionPolicyThresholdProvenanceDto = Record<string, number>;

export type WorkforceReductionPolicyRuleVersionRecordDto = {
  ruleVersionId: string;
  ruleKey: string;
  ruleVersion: string;
  label: string;
  clauseIds: string[];
  sourceArtifactIds: string[];
  sourceLocatorIds: string[];
  ruleJson: Record<string, unknown>;
  /** Field path -> triple index. Null for rules created before this binding. */
  thresholdProvenance: WorkforceReductionPolicyThresholdProvenanceDto | null;
  approvedForPublication: boolean;
  approvalRecordId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkforceReductionPolicyPackApprovalSummaryDto = {
  approvedClauseCount: number;
  totalClauseCount: number;
  approvedRuleVersionCount: number;
  totalRuleVersionCount: number;
  readyForPublication: boolean;
};

export type WorkforceReductionPolicyPackRecordDto = {
  policyPackId: string;
  /** Owning scope of this row — see WorkforceReductionPolicyOwnerScopeDto. */
  ownerScope: WorkforceReductionPolicyOwnerScopeDto;
  label: string;
  description: string | null;
  countryCode: string | null;
  subnationalCode: string | null;
  jurisdictionKey: string;
  packKind: WorkforceReductionPolicyPackKindDto;
  policyType: WorkforceReductionPolicyTypeDto;
  createdByUserId: string | null;
  clauses: WorkforceReductionPolicyClauseRecordDto[];
  ruleVersions: WorkforceReductionPolicyRuleVersionRecordDto[];
  approvalSummary: WorkforceReductionPolicyPackApprovalSummaryDto;
  createdAt: string;
  updatedAt: string;
};

export type WorkforceReductionPublishedPolicyPackProvenanceRecordDto = {
  provenanceId: string;
  clauseId: string | null;
  sourceArtifactId: string;
  sourceLocatorId: string | null;
  sourceLocatorLabel: string | null;
  sourceDocumentTitle: string | null;
  sourceDocumentFilename: string | null;
  sourceDocumentSha256: string | null;
  canonicalUrl: string | null;
  canonicalOrigin: string | null;
  pageNumberStart: number | null;
  pageNumberEnd: number | null;
  sectionLabel: string | null;
  excerpt: string | null;
  packPublicationApprovalRecordId: string | null;
  packPublicationApprovalActorUserId: string | null;
  publishedByUserId: string | null;
  unresolvedReason: string | null;
};

export type WorkforceReductionPublishedPolicyPackRecordDto = {
  publishedPolicyPackId: string;
  policyPackId: string;
  policyPackVersion: string;
  label: string;
  jurisdictionKey: string;
  countryCode: string | null;
  subnationalCode: string | null;
  packKind: WorkforceReductionPolicyPackKindDto;
  policyType: WorkforceReductionPolicyTypeDto;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  publishedAt: string;
  publishedByUserId: string | null;
  sourceArtifactIds: string[];
  sourceLocatorIds: string[];
  clauseIds: string[];
  ruleVersionIds: string[];
  publicationApprovalRecordId: string | null;
  publicationApprovalActorUserId: string | null;
  provenance: WorkforceReductionPublishedPolicyPackProvenanceRecordDto[];
};

export type WorkforceReductionPublishedPolicyPackResolutionDto = {
  status: 'resolved' | 'unsupported' | 'requires_legal_review';
  reasonCode: string | null;
  effectiveDate: string;
  publishedPacks: WorkforceReductionPublishedPolicyPackRecordDto[];
};

export type WorkforceReductionPolicyControlPlanePermissionsDto = {
  canWrite: boolean;
  canPublish: boolean;
};

export type WorkforceReductionPolicyControlPlaneViewDto = {
  permissions: WorkforceReductionPolicyControlPlanePermissionsDto;
  sourceArtifacts: WorkforceReductionPolicySourceArtifactRecordDto[];
  draftPacks: WorkforceReductionPolicyPackRecordDto[];
  publishedPacks: WorkforceReductionPublishedPolicyPackRecordDto[];
};

export type WorkforceReductionComplianceOverrideStatusDto = 'pending' | 'approved' | 'rejected';

export type WorkforceReductionComplianceOverrideDecisionDto = 'allow_progression';

export type WorkforceReductionComplianceOverrideDto = {
  overrideId: string;
  programId: string;
  snapshotId: string | null;
  assessmentId: string;
  status: WorkforceReductionComplianceOverrideStatusDto;
  decision: WorkforceReductionComplianceOverrideDecisionDto;
  rationale: string;
  requestedByUserId: string | null;
  approvedByUserId: string | null;
  createdAt: string;
  decidedAt: string | null;
};

export type WorkforceReductionComplianceAssessmentDto = {
  assessmentId: string;
  programId: string;
  snapshotId: string | null;
  status: WorkforceReductionComplianceAssessmentStatusDto;
  requiresLegalReview: boolean;
  jurisdictionProfile: WorkforceReductionJurisdictionProfileDto;
  policyPacks: WorkforceReductionPublishedPolicyPackDto[];
  triggeredRules: WorkforceReductionComplianceRuleVersionDto[];
  blockers: WorkforceReductionComplianceBlockerDto[];
  obligations: WorkforceReductionComplianceObligationDto[];
  deadlines: WorkforceReductionComplianceDeadlineDto[];
  missingData: WorkforceReductionComplianceMissingDataDto[];
  citations: WorkforceReductionComplianceCitationDto[];
  proof: WorkforceReductionComplianceProofDto;
  activeOverride: WorkforceReductionComplianceOverrideDto | null;
  computedAt: string;
};

/**
 * THE EXECUTABLE THRESHOLDS OF A RULE — the numbers that actually decide
 * outcomes, and therefore the ones that must be traceable to a source.
 *
 * THIS IS THE SINGLE AUTHORITY. It lives beside the DTO so the control plane
 * (which refuses a draft whose thresholds are unbound) and any client (which
 * would otherwise guess at the same list) read the SAME enumeration. A client
 * carrying its own copy is how a bundle comes to pass its own preflight and then
 * be refused by the server: the two lists drift, and the documented
 * author-and-rerun path stops working.
 *
 * Derived from the runtime parsers themselves (`parseWarnThresholdRule` and
 * `parseCollectiveConsultationRule` in the compliance service), so it cannot
 * drift from what the runtime really executes: if a field is not read by a
 * parser it is not an executable threshold, and if it is, it appears here.
 *
 * A field counts when the key is PRESENT in `ruleJson`, whatever its value.
 * `minimumSiteEmployees: null` is not an absence, it is the assertion "this
 * regime imposes no site-size condition" — an assertion that needs a source
 * exactly as much as a number does.
 */
export function executableThresholdFieldPaths(ruleJson: unknown): string[] {
  const rule = (ruleJson ?? {}) as Record<string, unknown>;
  const kind = String(rule?.kind ?? '').trim().toLowerCase();
  const present = (field: string) =>
    Boolean(rule) && typeof rule === 'object' && Object.prototype.hasOwnProperty.call(rule, field);
  if (kind === 'threshold') {
    return ['minimumAffectedEmployees', 'minimumSiteEmployees', 'noticeDays', 'consultationDays']
      .filter(present);
  }
  if (kind === 'threshold_tiers') {
    const tiers = Array.isArray(rule?.tiers) ? (rule.tiers as unknown[]) : [];
    return tiers.flatMap((tier, index) =>
      ['minimumAffectedEmployees', 'consultationDays']
        .filter(
          (field) =>
            Boolean(tier) &&
            typeof tier === 'object' &&
            Object.prototype.hasOwnProperty.call(tier as object, field),
        )
        .map((field) => `tiers.${index}.${field}`),
    );
  }
  return [];
}

/** Normalizes a persisted binding, rejecting anything that is not a clean map. */
export function readThresholdProvenance(
  value: unknown,
): WorkforceReductionPolicyThresholdProvenanceDto | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out: Record<string, number> = {};
  for (const [field, index] of Object.entries(value as Record<string, unknown>)) {
    if (typeof index !== 'number' || !Number.isInteger(index) || index < 0) return null;
    out[field] = index;
  }
  return out;
}

/**
 * Is every executable threshold in this rule bound to one of the rule's own
 * triples? Returns the field paths that are NOT, so callers can say which.
 *
 * This is deliberately a STRUCTURAL check — that a named, index-valid source
 * stands behind each number. Whether that source actually SUPPORTS the number
 * is a human judgment, recorded by the reviewer against this binding at
 * approval time. Digit matching was tried and removed: it accepted "60 days of
 * notice" as support for "60 affected employees", which is not evidence.
 */
export function unboundThresholdFields(args: {
  ruleJson: unknown;
  thresholdProvenance: unknown;
  tripleCount: number;
}): string[] {
  const fields = executableThresholdFieldPaths(args.ruleJson);
  if (fields.length === 0) return [];
  const bindings = readThresholdProvenance(args.thresholdProvenance);
  if (!bindings) return fields;
  return fields.filter((field) => {
    const index = bindings[field];
    return typeof index !== 'number' || index < 0 || index >= args.tripleCount;
  });
}

/**
 * THE EFFECTIVE-WINDOW PREDICATE for a published policy pack row — the exact
 * rule `resolvePublishedPolicyPackStack` uses to decide whether a row is part
 * of the stack on a given date. It lives here, beside the DTOs, for the same
 * reason `executableThresholdFieldPaths` does: the control plane consumes it
 * server-side, and window-boundary clients (the policy-pack importer's
 * probe-window derivation) must read the SAME convention rather than carrying
 * a copy that can drift.
 *
 * THE CONVENTION, PINNED: both boundaries are INCLUSIVE. A row is active ON
 * its effectiveStartDate, still active ON its effectiveEndDate, and out of the
 * stack the instant after — so post-expiry composition begins at end + 1ms,
 * never on the end date itself. An open-ended row (null effectiveEndDate)
 * never exits. Comparisons are millisecond instants; an unparseable start or
 * target date is never active (fail-closed).
 */
export function isPublishedPolicyPackWindowActive(
  row: { effectiveStartDate?: unknown; effectiveEndDate?: unknown } | null | undefined,
  effectiveDate: unknown,
): boolean {
  const target = new Date(effectiveDate as string | number | Date).getTime();
  const start = new Date(row?.effectiveStartDate as string | number | Date).getTime();
  const end = row?.effectiveEndDate
    ? new Date(row.effectiveEndDate as string | number | Date).getTime()
    : null;
  if (!Number.isFinite(target) || !Number.isFinite(start)) return false;
  if (target < start) return false;
  if (end != null && Number.isFinite(end) && target > end) return false;
  return true;
}

/**
 * The FIRST INSTANT a bounded window is no longer active — where post-expiry
 * stack composition begins. This is the exit-transition counterpart of a row's
 * effectiveStartDate: the resolver's answer can change at both, so a probe set
 * that covers activations must cover these too.
 *
 * Derived from the SAME inclusive-end convention as
 * `isPublishedPolicyPackWindowActive` (the window is active AT its end date,
 * so the boundary instant is end + 1ms) and pinned against that predicate by
 * test. Null for an open-ended window (it never exits) and for an unparseable
 * end date (no honest boundary to name).
 */
export function firstInstantAfterPolicyPackWindow(
  row: { effectiveEndDate?: unknown } | null | undefined,
): string | null {
  const endRaw = row?.effectiveEndDate;
  if (!endRaw) return null;
  const end = new Date(endRaw as string | number | Date).getTime();
  if (!Number.isFinite(end)) return null;
  return new Date(end + 1).toISOString();
}

/**
 * WHICH POLICY TYPE THE ASSESSMENT DISPATCHER WILL ACTUALLY RUN FOR A COUNTRY.
 *
 * THE SINGLE AUTHORITY for that pairing. `assessProgramCompliance` in
 * workforce-reduction.service.ts selects the policy type from the program's
 * country and resolves nothing at all when the pairing is unsupported, so a pack
 * published outside these pairs is unreachable in production no matter how
 * cleanly it imports.
 *
 * It lives here, not in the service, because the control-plane readback must
 * consult it too and the service already depends on the control plane —
 * importing back the other way would be a cycle. The dispatcher consumes this
 * function; the readback consumes this function; there is no second country
 * list anywhere.
 *
 * Country codes are matched exactly as the dispatcher matches them. Note `UK`,
 * not `GB`: that is the value the dispatcher tests, so it is the value that
 * makes a pack reachable, and a pack published under `GB` is silently
 * unreachable however correct its contents.
 */
export function dispatcherPolicyTypeForCountry(
  countryCode: string | null | undefined,
): WorkforceReductionPolicyTypeDto | null {
  const country = String(countryCode ?? '').trim().toUpperCase();
  if (country === 'US') return 'warn_notice';
  if (country === 'UK') return 'collective_consultation';
  return null;
}

/**
 * Would the dispatcher ever run this policy type for this country? A readback
 * that reports success for a pairing this rejects is reporting a runtime the
 * program can never reach.
 */
export function isDispatcherSupportedPairing(
  countryCode: string | null | undefined,
  policyType: string | null | undefined,
): boolean {
  const expected = dispatcherPolicyTypeForCountry(countryCode);
  if (!expected) return false;
  return expected === String(policyType ?? '').trim().toLowerCase();
}
